import React, { useRef, useMemo } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Instance, Instances, PerspectiveCamera } from '@react-three/drei';
import * as THREE from 'three';

// ==========================================
// 1. THE INJECTOR TUNNEL (Hero)
// ==========================================
const TunnelRings = ({ count = 30, spacing = 5 }) => {
    const group = useRef();
    
    // Create ring instances
    const instances = useMemo(() => {
        const temp = [];
        for (let i = 0; i < count; i++) {
            const z = -i * spacing;
            const scale = 1 + Math.random() * 0.5;
            temp.push({ 
                position: [0, 0, z], 
                rotation: [0, 0, Math.random() * Math.PI],
                scale: [scale, scale, 1] 
            });
        }
        return temp;
    }, [count, spacing]);

    useFrame((state) => {
        if (!group.current) return;
        // Subtle rotation of the whole tunnel
        group.current.rotation.z = state.clock.elapsedTime * 0.05;
    });

    return (
        <group ref={group}>
            <Instances range={count}>
                <torusGeometry args={[4, 0.05, 16, 50]} />
                <meshBasicMaterial color="#666" transparent opacity={0.5} blending={THREE.AdditiveBlending} />
                {instances.map((data, i) => (
                    <Instance key={i} {...data} />
                ))}
            </Instances>
            {/* Light bars for speed sensation */}
             <Instances range={count}>
                <boxGeometry args={[0.1, 2, 8]} />
                <meshBasicMaterial color="#fff" />
                {instances.map((data, i) => (
                    <Instance 
                        key={i} 
                        position={[
                            Math.cos(i) * 4, 
                            Math.sin(i) * 4, 
                            data.position[2]
                        ]} 
                        rotation={[0, 0, i]}
                    />
                ))}
            </Instances>
        </group>
    );
};

// ==========================================
// SCENE MANAGER
// ==========================================
 const ColliderExperience = () => {
    const camRef = useRef();
    const tunnelRef = useRef();
    const { pointer } = useThree(); // Get mouse pointer (-1 to 1)

    useFrame((state) => {
        // Global Scroll Mapping
        const scrollY = window.scrollY; 
        
        // CAMERA LOGIC
        // Phase 1: The Tunnel (0 - 1000px)
        const targetZ = 10 - (scrollY * 0.015);
        if (camRef.current) {
            // Smooth damp the camera
            camRef.current.position.z = THREE.MathUtils.lerp(camRef.current.position.z, Math.max(-20, targetZ), 0.1);
            
            // Camera shake based on speed + MOUSE LOOK (Lorentz Force feel)
            // We use pointer.x and pointer.y to gently sway the camera
            const mouseX = pointer.x * 2; 
            const mouseY = pointer.y * 2;
            
            const shake = Math.sin(state.clock.elapsedTime * 20) * (scrollY > 100 ? 0.005 : 0);
            
            camRef.current.position.x = THREE.MathUtils.lerp(camRef.current.position.x, shake + mouseX, 0.05);
            camRef.current.position.y = THREE.MathUtils.lerp(camRef.current.position.y, mouseY, 0.05);
            
            // Minimal rotation to follow mouse
            camRef.current.rotation.y = THREE.MathUtils.lerp(camRef.current.rotation.y, pointer.x * -0.1, 0.05);
            camRef.current.rotation.x = THREE.MathUtils.lerp(camRef.current.rotation.x, pointer.y * 0.1, 0.05);
        }

        // Phase 2: Visibility culling
        if (tunnelRef.current) {
             tunnelRef.current.position.z = scrollY * 0.01; 
             // Tunnel also subtly bends based on mouse
             tunnelRef.current.rotation.y = THREE.MathUtils.lerp(tunnelRef.current.rotation.y, pointer.x * 0.2, 0.02);
        }
    });

    return (
        <>
            <PerspectiveCamera ref={camRef} makeDefault position={[0, 0, 10]} fov={40} />
            <fog attach="fog" args={['#000', 5, 40]} />
            <ambientLight intensity={0.1} />
            <spotLight position={[10, 10, 10]} angle={0.5} penumbra={1} intensity={0.5} color="#a0a0ff" />
            <pointLight position={[-10, -10, -10]} intensity={0.5} color="#444" />

            <group ref={tunnelRef}>
                <TunnelRings />
            </group>
        </>
    );
};

export default ColliderExperience;
